/** @format */

import io from "socket.io-client";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import { fToNow } from "../utils/format-time";
import Scrollbar from "../../components/scrollbar/Scrollbar";
import Iconify from "../../components/iconify/Iconify";
import { useDispatch, useSelector } from "react-redux";
import { WEB_SOCKET_URL } from "../../axiosConfig/apiUrl";
import { AppDispatch } from "../../redux/store/store";
import {
	addNotification,
	clearAllNotification,
	deleteNotification,
	markAllNotificationAsRead,
	markNotificationAsRead,
} from "../../redux/reducers/notificationReducer";
import { selectedNotifications } from "../../redux/selectors/notificationSelector";
// import { selectCurrentUser } from '../../redux/selectors/userSelector';
import { colors } from "../types/constant";
import { useMediaQuery, useTheme } from "@mui/material";
import { getUser } from "../../redux/actions/userActions";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { updateSubscriptionStatus } from "../../redux/reducers/subscriptionReducer";
import { updateContributionChartRealTime } from "../../redux/actions/contributionAction";
import { fetchCircleInvitations } from "../../redux/actions/circleInvitationAction";

export default function Notifications() {
	const dispatch = useDispatch<AppDispatch>();

	const userData = useSelector(selectCurrentUser);

	const notifications = useSelector(selectedNotifications);

	const [showAllNotifications, setShowAllNotifications] = useState(false);

	// const [notifications, setNotifications] = useState<INotifications[]>([]);

	const totalUnRead = notifications.filter((item: any) => item.isUnRead).length;

	const [open, setOpen] = useState(null);

	useEffect(() => {
		const token = localStorage.getItem("idToken");

		if (!token) {
			return;
		}

		const ws = new WebSocket(WEB_SOCKET_URL, token);

		let pingInterval: number;

		// send a ping every 2 minutes to prevent api gateway from idle timeout
		pingInterval = window.setInterval(
			() => {
				if (ws.readyState === WebSocket.OPEN) {
					ws.send(JSON.stringify({ type: "ping" }));
				}
			},
			2 * 60 * 1000,
		);

		ws.onmessage = async (event) => {
			let notification: any;
			try {
				const msg = JSON.parse(event.data);
				// your SNS→Lambda likely wraps under .content
				notification = msg.content ?? msg;
			} catch (err) {
				return;
			}

			switch (notification.type) {
				case "subscription":
					await dispatch(
						addNotification({
							id: notification.data.subscriptionId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "new-application":
					await dispatch(
						addNotification({
							id: notification.data.applicationId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "verification-status":
					await dispatch(
						addNotification({
							id: notification.data.applicationId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "application-approved":
					if (notification.data.userId) {
						await dispatch(getUser(notification.data.userId));
					}

					if (notification.data.notificationMessage) {
						await dispatch(
							addNotification({
								id: notification.data.applicationId,
								message: notification.data.notificationMessage,
								isUnRead: true,
							}),
						);
					}
					break;
				case "add member request notification":
					await dispatch(
						addNotification({
							id: notification.data.addRequestId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "join request notification":
					await dispatch(
						addNotification({
							id: notification.data.joinRequestId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "join request leader notification":
					await dispatch(
						addNotification({
							id: notification.data.joinRequestFromUserId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "joinRequestApproved":
					await dispatch(
						addNotification({
							id: notification.data.requestId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					// Refresh user data to update unions array
					if (userData?._id) {
						await dispatch(getUser(userData._id));
					}
					break;
				case "joinRequestRejected":
					await dispatch(
						addNotification({
							id: notification.data.requestId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "add member request leader notification":
					await dispatch(
						addNotification({
							id: notification.data.addRequestFromLeaderId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "updated-payment-method":
					await dispatch(
						addNotification({
							id: notification.data.paymentMethodId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "subscription-payment-failed":
					await dispatch(
						addNotification({
							id: notification.data.invoiceId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "subscriptionStatusChanged":
					dispatch(
						updateSubscriptionStatus({
							subscriptionId: notification.data.subscriptionCancelledId,
							status: notification.data.status,
						}),
					);
					await dispatch(
						addNotification({
							id: notification.data.subscriptionCancelledId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "acceptedLeaderAddRequest":
					await dispatch(
						addNotification({
							id: notification.data.requestId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "newMemberJoinedCircle":
					await dispatch(
						addNotification({
							id: notification.data.requestId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "updated-tokens":
					console.log("Token have been refreshed by the server");
					break;
				case "contributionSuccess":
					await dispatch(
						addNotification({
							id: notification.data.contributionId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "memberContributionSuccess":
					await dispatch(
						addNotification({
							id: notification.data.contributionId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);

					if (notification.data.chartUpdate?.silent) {
						dispatch(
							updateContributionChartRealTime({
								unionId: notification.data.chartUpdate.unionId,
								contributionStats:
									notification.data.chartUpdate.contributionStats,
								newContribution: notification.data.chartUpdate.newContribution,
							}),
						);
					}
					break;
				case "contributionCycleStarted":
					await dispatch(
						addNotification({
							id: notification.data.cycleId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "contributionCycleAutoEndWarning":
					await dispatch(
						addNotification({
							id: notification.data.contributionCycleId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "contributionCycleEnded":
					await dispatch(
						addNotification({
							id: notification.data.cycleId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "circle-invitation":
					await dispatch(
						addNotification({
							id: notification.data.invitationId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					// Refresh so the dashboard invitation CTA appears without a reload
					dispatch(fetchCircleInvitations());
					break;
				case "circle-invitation-accepted":
					await dispatch(
						addNotification({
							id: notification.data.invitationId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "circle-invitation-pending-approval":
					await dispatch(
						addNotification({
							id: notification.data.invitationId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				case "circle-membership-added":
					await dispatch(
						addNotification({
							id: notification.data.circleId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					// e.g. a leader/admin approved their invitation; refresh its status
					dispatch(fetchCircleInvitations());
					// Refresh user data so the new circle appears in their circles list
					if (userData?._id) {
						await dispatch(getUser(userData._id));
					}
					break;
				case "circle-invitation-rejected":
					await dispatch(
						addNotification({
							id: notification.data.invitationId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					dispatch(fetchCircleInvitations());
					break;
				case "circle-invitation-denied":
				case "circle-invitation-approved":
					await dispatch(
						addNotification({
							id: notification.data.circleId,
							message: notification.data.notificationMessage,
							isUnRead: true,
						}),
					);
					break;
				default:
					break;
			}
		};

		ws.onerror = (err) => {
			// to do
		};

		ws.onclose = (ev) => {
			// to do
		};

		// cleanup on unmount or token change
		return () => {
			clearInterval(pingInterval);
			ws.close();
		};
	}, [dispatch]);

	const handleNotificationClick = (id: any) => {
		dispatch(markNotificationAsRead(id));
	};

	const handleDeleteNotification = (notificationId: string) => {
		dispatch(deleteNotification(notificationId));
	};

	const handleClearNotification = () => {
		dispatch(clearAllNotification());
	};

	const handleOpen = (event: any) => {
		setOpen(event.currentTarget);
	};

	const handleClose = () => {
		setOpen(null);
	};

	const handleMarkAllAsRead = () => {
		dispatch(markAllNotificationAsRead());
	};

	const toggleShowAllNotifications = () => {
		setShowAllNotifications(true);
	};
	const theme = useTheme();

	const isXs = useMediaQuery(theme.breakpoints.down("xs"));

	const isSm = useMediaQuery(theme.breakpoints.down("sm"));

	const isMd = useMediaQuery(theme.breakpoints.down("md"));

	const maxHeight = 500;

	const notificationHeight = 200;

	const maxNotificationToShow =
		isXs ? 5
		: isSm ? 10
		: isMd ? 15
		: 20;

	// new notifications
	const newNotifications = notifications.filter((n) => n.isUnRead);
	// old notifications
	const oldNotifications = notifications.filter((n) => !n.isUnRead);

	const displayedNewNotifications =
		showAllNotifications ? newNotifications : (
			newNotifications.slice(0, maxNotificationToShow)
		);

	const displayedOldNotifications =
		showAllNotifications ? oldNotifications : (
			oldNotifications.slice(0, maxNotificationToShow)
		);

	// Calculate height based on both new and old notifications
	const totalDisplayed =
		displayedNewNotifications.length + displayedOldNotifications.length;

	const calculatedHeight: number = Math.min(
		totalDisplayed * notificationHeight,
		maxHeight,
	);

	return (
		<>
			<IconButton color={open ? "primary" : "default"} onClick={handleOpen}>
				<Badge badgeContent={totalUnRead} color="error">
					<Iconify width={24} icon="solar:bell-bing-bold-duotone" />
				</Badge>
			</IconButton>
			{userData?.stripeCustomerId && (
				<IconButton color={open ? "primary" : "default"}>
					<Badge color="error">
						<Avatar
							src="/assets/icons/verified-animated-icon.gif"
							sx={{
								width: 30,
								height: 30,
							}}
						/>
					</Badge>
				</IconButton>
			)}

			<Popover
				open={!!open}
				anchorEl={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: {
							mt: 1.5,
							ml: 0.75,
							width: 360,
						},
					},
				}}>
				<Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
					<Box sx={{ flexGrow: 1 }}>
						<Typography variant="subtitle1">Notifications</Typography>
						<Typography variant="body2" sx={{ color: "text.secondary" }}>
							You have {totalUnRead} unread messages
						</Typography>
					</Box>

					{totalUnRead > 0 && (
						<Tooltip title=" Mark all as read">
							<IconButton color="primary" onClick={handleMarkAllAsRead}>
								<Iconify icon="eva:done-all-fill" />
							</IconButton>
						</Tooltip>
					)}
				</Box>

				<Divider sx={{ borderStyle: "dashed" }} />

				<Scrollbar
					sx={{
						height: calculatedHeight - 80,
						overflowY: "auto",
					}}>
					{displayedNewNotifications.length > 0 && (
						<List
							disablePadding
							subheader={
								<ListSubheader
									disableSticky
									sx={{ py: 1, px: 2.5, typography: "overline" }}>
									New
								</ListSubheader>
							}>
							{displayedNewNotifications.map((notification) => (
								<NotificationItem
									key={`new-${notification.id}`}
									notification={notification}
									onClick={() => handleNotificationClick(notification.id)}
									onDelete={() => handleDeleteNotification(notification.id)}
								/>
							))}
						</List>
					)}

					{displayedOldNotifications.length > 0 && (
						<List
							disablePadding
							subheader={
								<ListSubheader
									disableSticky
									sx={{ py: 1, px: 2.5, typography: "overline" }}>
									Before that
								</ListSubheader>
							}>
							{displayedOldNotifications.map((notification) => (
								<NotificationItem
									key={`old-${notification.id}`}
									notification={notification}
									onClick={() => handleNotificationClick(notification.id)}
									onDelete={() => handleDeleteNotification(notification.id)}
								/>
							))}
						</List>
					)}
				</Scrollbar>

				<Divider sx={{ borderStyle: "dashed" }} />

				<Box sx={{ p: 1 }}>
					{/* <Button onClick={toggleShowAllNotifications} fullWidth disableRipple>
            {showAllNotifications ? "Show Less" : "View All"}
          </Button> */}
					<Button onClick={handleClearNotification}>Clear All</Button>
				</Box>
			</Popover>
		</>
	);
}

NotificationItem.propTypes = {
	notification: PropTypes.shape({
		createdAt: PropTypes.instanceOf(Date),
		id: PropTypes.string,
		isUnRead: PropTypes.bool,
		title: PropTypes.string,
		description: PropTypes.string,
		type: PropTypes.string,
		avatar: PropTypes.any,
	}),
	onDelete: PropTypes.any,
	onClick: PropTypes.any,
};

function NotificationItem({ notification, onDelete, onClick }: any) {
	const { title } = renderContent(notification);

	const dispatch = useDispatch();

	const handleNotificationClick = () => {
		dispatch(markNotificationAsRead(notification.id));
	};

	return (
		<ListItemButton
			onClick={handleNotificationClick}
			sx={{
				py: 1.5,
				px: 2.5,
				mt: "1px",
				...(notification.isUnRead && {
					bgcolor: "action.selected",
				}),
			}}>
			<ListItemAvatar>
				<Avatar
					sx={{ bgcolor: colors.white }}
					src={"/assets/images/avatars/admin-img-1.jpeg"}
				/>
			</ListItemAvatar>
			<ListItemText
				primary={title}
				secondary={
					<Typography
						onClick={onClick}
						variant="caption"
						sx={{
							mt: 0.5,
							display: "flex",
							alignItems: "center",
							color: "text.disabled",
						}}>
						<Iconify
							icon="eva:clock-outline"
							sx={{ mr: 0.5, width: 16, height: 16 }}
						/>
						<IconButton onClick={onDelete}>
							<Iconify
								icon="material-symbols:delete"
								sx={{ mr: 0.5, width: 16, height: 16, color: "red" }}
							/>
						</IconButton>
						{fToNow(notification.createdAt)}
					</Typography>
				}
			/>
		</ListItemButton>
	);
}

function renderContent(notification: any) {
	const title = (
		<Typography variant="subtitle2">
			{"Admin:"}
			<Typography
				component="span"
				variant="body2"
				sx={{ color: "text.secondary" }}>
				&nbsp; {notification.message}
			</Typography>
		</Typography>
	);

	if (notification.type === "chat_message") {
		return {
			avatar: (
				<img
					alt={notification.title}
					src="/assets/icons/ic_notification_chat.svg"
				/>
			),
			title,
		};
	}
	return {
		avatar:
			notification.avatar ?
				<img
					alt={notification.title}
					src={"/assets/images/avatars/admin-img-1.jpeg"}
				/>
			:	null,
		title,
	};
}
